import React from 'react';
import moment from 'moment';

import useAppContext from 'flow/hooks/useAppContext';
import { getPrettyDuration } from 'flow/utils/call-utils';
import IntervalRenderer from 'flow/components/custom-components/interval-renderer';
import CallTitleItem from './CallTitleItem';

interface Props {
    direction: string;
    isOnHold?: boolean;
    startTime?: Date;
    isEnded?: boolean;
    failCause?: string;
    isAwaitingTransfer?: boolean;
    useOmniErrandTerminologyForIncomingItem?: boolean;
}

//https://jssip.net/documentation/3.5.x/api/causes/#sip_error_causes
const geti18KeyForUIFriendlyEndCause = (failCause: string) => {
    switch (failCause) {
        case 'Busy':
            return 'callcontrol.busy';
        case 'Unavailable':
            return 'callcontrol.unavailable';
        case 'Not Found':
            return 'callcontrol.unavailable';
        default:
            return 'callcontrol.call_ended';
    }
};

const CallTitle = ({
    direction,
    isOnHold,
    startTime,
    isEnded,
    failCause,
    isAwaitingTransfer,
    useOmniErrandTerminologyForIncomingItem, //At the time of writing relevant for incoming 'call' only
}: Props) => {
    const { i18next } = useAppContext();

    if (isAwaitingTransfer) {
        return (
            <CallTitleItem
                dataTestId="call-title-waiting-for-transfer"
                className="text-white"
            >
                {i18next.t('callcontrol.waiting_for_transfer')}
            </CallTitleItem>
        );
    }

    if (isEnded || failCause) {
        return (
            <CallTitleItem
                dataTestId={
                    failCause === 'Busy'
                        ? 'call-title-busy'
                        : 'call-title-ended'
                }
                className="text-white"
            >
                {i18next.t(geti18KeyForUIFriendlyEndCause(failCause))}
            </CallTitleItem>
        );
    }

    if (isOnHold) {
        return (
            <CallTitleItem
                dataTestId="call-title-on-hold"
                className="text-white tabular-nums"
            >
                {i18next.t('callcontrol.parked_call')}
            </CallTitleItem>
        );
    }

    if (startTime) {
        return (
            <CallTitleItem
                dataTestId="call-title-duration"
                className="ps-1 text-white tabular-nums"
            >
                <IntervalRenderer interval={1000}>
                    {() =>
                        getPrettyDuration(
                            moment.duration(moment().diff(moment(startTime))),
                        )
                    }
                </IntervalRenderer>
            </CallTitleItem>
        );
    }

    if (direction === 'incoming') {
        return (
            <CallTitleItem
                dataTestId="call-title-incoming"
                className="text-yellow"
            >
                {useOmniErrandTerminologyForIncomingItem
                    ? 'Incoming errand'
                    : i18next.t('callcontrol.incoming_call')}
            </CallTitleItem>
        );
    }

    return (
        <CallTitleItem
            dataTestId="call-title-outgoing"
            className="p-2 text-white"
        >
            {i18next.t('callcontrol.outgoing_call')}
        </CallTitleItem>
    );
};

export default React.memo(CallTitle);
